import { memo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Home, Search, Plus, Heart, User, ShoppingCart } from 'lucide-react'
import { clsx } from 'clsx'
import { useAuthStore, isStoreAccount } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { useCartStore } from '@/store/cartStore'
import { useNotificationStore } from '@/store/notificationStore'

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  isActive: boolean
  onClick?: () => void
}

const NavItem = memo(function NavItem({ to, icon, label, isActive, onClick }: NavItemProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      aria-label={label}
      className={clsx(
        'flex flex-col items-center justify-center gap-1 py-2 flex-1',
        isActive ? 'text-primary-500' : 'text-slate-500'
      )}
    >
      {icon}
      <span className="text-xs">{label}</span>
    </Link>
  )
})

export function BottomNav() {
  const location = useLocation()
  const { isAuthenticated, profile } = useAuthStore()
  const { openModal } = useUIStore()
  const { itemCount, openDrawer: openCart } = useCartStore()
  const unreadCount = useNotificationStore((s) => s.unreadCount)

  const handleAuthRequired = () => {
    if (!isAuthenticated) {
      openModal('login')
    }
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-40 bottom-nav safe-bottom">
      <div className="flex items-center justify-around">
        <NavItem
          to="/"
          icon={<Home className="w-5 h-5" />}
          label="Home"
          isActive={location.pathname === '/'}
        />

        <NavItem
          to="/browse"
          icon={<Search className="w-5 h-5" />}
          label="Browse"
          isActive={location.pathname === '/browse'}
        />

        {/* Center create button */}
        <Link
          to={isAuthenticated ? '/create' : '#'}
          onClick={() => !isAuthenticated && openModal('login')}
          className="flex items-center justify-center w-14 h-14 -mt-5 rounded-full bg-accent-500 text-white shadow-lg"
          aria-label="Create listing"
        >
          <Plus className="w-6 h-6" />
        </Link>

        {itemCount > 0 ? (
          <button
            onClick={openCart}
            aria-label="Cart"
            className="flex flex-col items-center justify-center gap-1 py-2 flex-1 text-slate-500 relative"
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="absolute -top-0.5 right-1/4 bg-accent-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
              {itemCount > 9 ? '9+' : itemCount}
            </span>
            <span className="text-xs">Cart</span>
          </button>
        ) : (
          <NavItem
            to={isAuthenticated ? '/watchlist' : '#'}
            icon={<Heart className="w-5 h-5" />}
            label="Saved"
            isActive={location.pathname === '/watchlist' || location.pathname === '/saved'}
            onClick={handleAuthRequired}
          />
        )}

        <Link
          to={isAuthenticated ? '/profile' : '#'}
          onClick={handleAuthRequired}
          aria-label={isStoreAccount(profile) ? 'Store' : 'Profile'}
          className={clsx(
            'flex flex-col items-center justify-center gap-1 py-2 flex-1 relative',
            location.pathname.startsWith('/profile') ? 'text-primary-500' : 'text-slate-500'
          )}
        >
          <User className="w-5 h-5" />
          {isAuthenticated && unreadCount > 0 && (
            <span className="absolute top-0.5 right-1/4 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white">
              <span className="sr-only">unread notifications</span>
            </span>
          )}
          <span className="text-xs">{isStoreAccount(profile) ? 'Store' : 'Profile'}</span>
        </Link>
      </div>
    </nav>
  )
}
